import z from 'zod'
import { nullSafeBoolean, nullSafeNumber } from '../shared/schema'

// All Soundcheck calibration lives on one document, settings/soundcheck, so a
// live operator can turn the notifications on and tune every confirmation
// period in one place without a deploy. Field names are unprefixed because the
// document already namespaces them.
//
// Health checks and telemetry run whether or not notifications are enabled; the
// only thing the switch changes is whether the dialog and banner appear. There
// is no blocking switch: AV validation and speech-driven participation depend on
// the same measurement, so turning it off never stops MicCheck.
export const schema = z.object({
  // Show the dialog and banner. Health checks and telemetry run when false.
  notificationsEnabled: nullSafeBoolean(false),
  // Account for elapsed time and evaluate pending checks on one controller clock.
  tickMS: nullSafeNumber(5000),
  // Allow microphone startup or restart to settle before confirming an unusable
  // track.
  micGraceMS: nullSafeNumber(5000),
  // Separate the two digital-zero reads that confirm digital_silence.
  silenceConfirmDelayMS: nullSafeNumber(5000),
  // Re-read an active track so a mic that dies mid-session is noticed.
  silencePollIntervalMS: nullSafeNumber(15000),
  // Allow server speech confirmation to arrive before a delivery failure.
  reachabilityDelayMS: nullSafeNumber(10000),
  // Retain recent local and server speech evidence for the delivery check.
  reachabilityWindowMS: nullSafeNumber(60000),
  // Require meaningful local speech before a delivery failure.
  reachabilityMinSpeechMS: nullSafeNumber(8000),
  // Qualify a speaking period for the transcript-flow wait.
  transcriptMinSpeechMS: nullSafeNumber(10000),
  // Allow transcript progress to arrive after a qualifying pause.
  noTranscriptAfterMS: nullSafeNumber(120000),
  // Detect unmuted solo discussion without any transcript flow.
  unmutedNoTranscriptAfterMS: nullSafeNumber(180000),
  // Report missing or inactive Secretary status.
  heartbeatTimeoutMS: nullSafeNumber(30000),
})

export const empty = Object.freeze(schema.parse({}))

export type FirestoreSettingsSoundcheck = z.infer<typeof schema>
